/**
 * disposal_daily_email_facility_log + disposal_daily_email_digest_log —
 * Phase 16 / Task 16.4 (DEC-161).
 *
 * Idempotency + observability for daily-email sends. Two scopes:
 *   - facility scope — one row per (facility, local_date)
 *   - digest   scope — one row per (company,  local_date)
 *
 * Platform-catalog tables (NO RLS). Writes only happen via the
 * SECURITY DEFINER `platform_disposal_daily_email_*_record_send` functions
 * (migrator-owned; INSERT + swallow unique_violation → return false). The
 * sweeper uses the return value as the "should I send?" atomic gate so
 * cross-task races can't produce duplicate sends. app_user gets
 * SELECT-only for the disposal portal to display send-history.
 *
 * NOT declared as pgTable(...) with a pgPolicy because the RLS posture is
 * "no policy = no cross-tenant access from app_user via RLS-enabled tables";
 * these tables have ROW LEVEL SECURITY disabled at the SQL layer. Reads
 * that need per-tenant scoping happen via SECURITY DEFINER helpers or via
 * an explicit JOIN to `disposal_facilities` / `disposal_companies` in the
 * route.
 */

import { date, numeric, pgTable, timestamp, uniqueIndex, uuid, integer } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { disposalCompanies, disposalFacilities } from './disposals.js';

export const disposalDailyEmailFacilityLog = pgTable(
  'disposal_daily_email_facility_log',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalFacilityId: uuid('disposal_facility_id')
      .notNull()
      .references(() => disposalFacilities.id, { onDelete: 'cascade' }),
    localDate: date('local_date').notNull(),
    sentAt: timestamp('sent_at', { withTimezone: true }).notNull().defaultNow(),
    recipientCount: integer('recipient_count').notNull(),
    deliveryCount: integer('delivery_count').notNull(),
    totalBbl: numeric('total_bbl', { precision: 12, scale: 2 }).notNull().default('0'),
  },
  (t) => [
    // Atomic idempotency key. SECURITY DEFINER _record_send function relies on
    // this UNIQUE to raise unique_violation → caught + swallowed → return false.
    uniqueIndex('disposal_daily_email_facility_log_key').on(t.disposalFacilityId, t.localDate),
  ],
);

export type DisposalDailyEmailFacilityLog = typeof disposalDailyEmailFacilityLog.$inferSelect;
export type NewDisposalDailyEmailFacilityLog = typeof disposalDailyEmailFacilityLog.$inferInsert;
export const disposalDailyEmailFacilityLogSelectSchema = createSelectSchema(
  disposalDailyEmailFacilityLog,
);
export const disposalDailyEmailFacilityLogInsertSchema = createInsertSchema(
  disposalDailyEmailFacilityLog,
);

export const disposalDailyEmailDigestLog = pgTable(
  'disposal_daily_email_digest_log',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    localDate: date('local_date').notNull(),
    sentAt: timestamp('sent_at', { withTimezone: true }).notNull().defaultNow(),
    recipientCount: integer('recipient_count').notNull(),
    facilityCount: integer('facility_count').notNull(),
    deliveryCount: integer('delivery_count').notNull(),
    totalBbl: numeric('total_bbl', { precision: 12, scale: 2 }).notNull().default('0'),
  },
  (t) => [uniqueIndex('disposal_daily_email_digest_log_key').on(t.disposalCompanyId, t.localDate)],
);

export type DisposalDailyEmailDigestLog = typeof disposalDailyEmailDigestLog.$inferSelect;
export type NewDisposalDailyEmailDigestLog = typeof disposalDailyEmailDigestLog.$inferInsert;
export const disposalDailyEmailDigestLogSelectSchema = createSelectSchema(
  disposalDailyEmailDigestLog,
);
export const disposalDailyEmailDigestLogInsertSchema = createInsertSchema(
  disposalDailyEmailDigestLog,
);
